import { AD_TAGS } from 'components/PaidPlacement';
import type CustomWindow from 'interfaces/Utils';
import Script from 'next/script';
import { useEffect, useState } from 'react';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

declare let window: CustomWindow;

export interface StnVideoProps {
  dataCy?: string;
  videoId: string;
  supressStnFloating?: boolean;
  adTags?: {
    name: string;
    slug: string;
  }[];
}

const StnVideo = ({ videoId, adTags, supressStnFloating, dataCy = '' }: StnVideoProps) => {
  const [apiReady, setApiReady] = useState(false);
  const [paywallHeight, setPaywallHeight] = useState(0);
  const [shouldPaywallFloat, setShouldPaywallFloat] = useState(true);
  const updateStnCid = adTags?.find((tag) => tag.name === AD_TAGS.NO_VIDEO_ADS);
  const updatedVideoId = updateStnCid ? videoId.replace(/-14497$/g, '-16481') : videoId;
  const { userFlags: userVariants } = useOptimizely();
  const disableDocking = (userVariants?.stnVideo_dockedpw as { variant: string })?.variant === 'on';

  useEffect(() => {
    window.setupS2NApi = (getS2NApiForPlayer: (player: Element) => void) => {
      const s2nPlayer = document.querySelector('.s2nPlayer');
      window.s2nApi = getS2NApiForPlayer(s2nPlayer) as unknown as { float: boolean; floatVerticalTranslate: number };
      setApiReady(true);
    };

    return () => {
      delete window.setupS2NApi;
    };
  }, []);

  useEffect(() => {
    const paywallResizeObserver = new ResizeObserver((entries) => {
      setPaywallHeight(entries[0].contentRect.height);
    });

    paywallResizeObserver.observe(document.querySelector('.paywallax-ii-selector'));

    return () => paywallResizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const pianoModalObserver = new MutationObserver((records) => {
      const modalWasAdded = records.some((mutation) => {
        const currentRecordAddedModal = Array.from(mutation.addedNodes).some((added) => {
          // Check if the node is an HTMLElement before accessing classList
          return added instanceof HTMLElement && added.classList.contains('tp-modal');
        });

        return currentRecordAddedModal;
      });

      if (modalWasAdded) {
        setShouldPaywallFloat(false);
        return;
      }

      const modalWasRemoved = records.some((mutation) => {
        const currentRecordRemovedModal = Array.from(mutation.removedNodes).some((removed) => {
          // Check if the node is an HTMLElement before accessing classList
          return removed instanceof HTMLElement && removed.classList.contains('tp-modal');
        });

        return currentRecordRemovedModal;
      });

      if (modalWasRemoved) {
        setShouldPaywallFloat(true);
        return;
      }
    });

    pianoModalObserver.observe(document.body, { childList: true });

    return () => pianoModalObserver.disconnect();
  }, []);

  useEffect(() => {
    if (apiReady) {
      const stickyFooterRendered = paywallHeight === 0 && supressStnFloating;
      window.s2nApi.floatVerticalTranslate = paywallHeight;
      // Disable docking if Optimizely variant is 'on', otherwise use existing logic
      if (disableDocking) {
        window.s2nApi.float = false;
      } else if (stickyFooterRendered) {
        window.s2nApi.float = false;
      } else {
        window.s2nApi.float = shouldPaywallFloat;
      }
    }
  }, [apiReady, paywallHeight, shouldPaywallFloat, disableDocking]);

  return (
    <>
      <Script
        src={`https://embed.sendtonews.com/player3/embedcode.js?SC=${updatedVideoId}`}
        strategy='afterInteractive'
        aria-label='Featured Video'
      />
      <div
        data-cy={dataCy}
        data-content-placement='stn-video'
        className={`stn-video-player s2nPlayer k-${updatedVideoId} impression-element`}
        data-type='float'
        data-s2n-api='setupS2NApi'
      />
    </>
  );
};

export default StnVideo;
